import { computed, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

import { anykrowdApi } from '@/api/anykrowdApi';
import useLoading from '@/shared/composables/useLoading';
import useToast from '@/shared/composables/useToast';
import { useAuthStore } from '@/stores/auth/authStore';
import useGeolocation, { GeoCoords } from '@/modules/safety/composables/useGeolocation';

export type IncidentType = 'medical' | 'lost_person' | 'theft' | 'aggression' | 'suspicious' | 'fire' | 'accident' | 'security' | 'general' | 'sos';

export type IncidentStatus = 'open' | 'assigned' | 'in_progress' | 'resolved' | 'cancelled';
export type IncidentPriority = 'p1' | 'p2' | 'p3';

export interface Incident {
	id: number;
	public_code: string;
	type: IncidentType;
	status: IncidentStatus;
	priority: IncidentPriority;
	description?: string | null;
	latitude?: number | null;
	longitude?: number | null;
	gps_accuracy?: number | null;
	location_updated_at?: string | null;
	zone_id?: number | null;
	source_app: string;
	source_module: string;
	assigned_to: number[];
	resolved_at?: string | null;
	cancelled_at?: string | null;
	created_at: string;
	updated_at: string;
	timeline?: Array<{
		kind: string;
		actor_type: string;
		actor_id?: number | null;
		payload?: any;
		created_at: string;
	}>;
}

export interface SafetyBroadcast {
	id: number;
	priority: 'info' | 'warning' | 'critical';
	title: string;
	body: string;
	expires_at?: string | null;
	created_at: string;
}

export interface SafetyConfig {
	enabled: boolean;
	incident_types: IncidentType[];
	quick_messages: Record<string, string[]>;
	info_cards: Array<{ title: string; body: string; icon?: string }>;
	gps_required: boolean;
	emergency_contacts: Array<{ label: string; value: string }>;
}

export interface ReportIncidentPayload {
	type: IncidentType;
	description?: string;
	latitude?: number;
	longitude?: number;
	gps_accuracy?: number;
	zone_id?: number;
	reporter_name?: string;
	reporter_email?: string;
	reporter_phone?: string;
}

export interface UpdateIncidentLocationPayload {
	latitude: number;
	longitude: number;
	gps_accuracy?: number;
}

// API calls
const getConfigApiCall = async (): Promise<SafetyConfig> => {
	const { data } = await anykrowdApi.get<{ code: string; data: SafetyConfig }>('/safety/config', true);
	return data.data;
};

const getMyReportsApiCall = async (): Promise<Incident[]> => {
	try {
		const { data } = await anykrowdApi.get<{ code: string; data: Incident[] }>('/safety/incidents/me', true);
		return data.data ?? [];
	} catch (error: any) {
		if (isSafetyDisabledError(error)) {
			return [];
		}

		throw error;
	}
};

const getReportApiCall = async (incidentId: number): Promise<Incident | null> => {
	try {
		const { data } = await anykrowdApi.get<{ code: string; data: Incident }>(`/safety/incidents/${incidentId}`, true);
		return data.data ?? null;
	} catch (error: any) {
		if (isSafetyDisabledError(error)) {
			return null;
		}

		throw error;
	}
};

const reportIncidentApiCall = async (payload: ReportIncidentPayload): Promise<Incident> => {
	const { data } = await anykrowdApi.post<{ code: string; data: Incident }>('/safety/incidents', payload, true);
	return data.data;
};

const reportSosApiCall = async (payload: Pick<ReportIncidentPayload, 'latitude' | 'longitude' | 'gps_accuracy' | 'description'>): Promise<Incident> => {
	const { data } = await anykrowdApi.post<{ code: string; data: Incident }>('/safety/incidents/sos', payload, true);
	return data.data;
};

const updateIncidentLocationApiCall = async (incidentId: number, payload: UpdateIncidentLocationPayload): Promise<Incident> => {
	const { data } = await anykrowdApi.post<{ code: string; data: Incident }>(`/safety/incidents/${incidentId}/location`, payload, true);
	return data.data;
};

const getActiveBroadcastsApiCall = async (): Promise<SafetyBroadcast[]> => {
	try {
		const { data } = await anykrowdApi.get<{ code: string; data: SafetyBroadcast[] }>('/safety/broadcasts/active', true);
		return data.data ?? [];
	} catch (error: any) {
		if (isSafetyDisabledError(error)) {
			return [];
		}

		throw error;
	}
};

const ackBroadcastApiCall = async (broadcastId: number): Promise<void> => {
	await anykrowdApi.post(`/safety/broadcasts/${broadcastId}/ack`, {}, true);
};

const isSafetyDisabledError = (error: any): boolean => error?.response?.data?.code === 'safety_disabled';
const isLocationTrackingStoppedError = (error: any): boolean => error?.response?.data?.code === 'location_tracking_stopped';

// Shared helpers
const { presentLoading } = useLoading();
const { presentToast } = useToast();
let stopActiveSosTracker: (() => void) | null = null;

const getSafetyConfig = () => {
	const config = ref<SafetyConfig | null>(null);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ['safetyConfig'],
		queryFn: getConfigApiCall,
		staleTime: 0,
		cacheTime: 1000 * 60 * 30,
		refetchOnMount: 'always',
		refetchOnWindowFocus: 'always',
	});

	watch(
		data,
		() => {
			if (data.value) {
				config.value = data.value;
			}
		},
		{ immediate: true },
	);

	return {
		config,
		isFetchingConfig: computed(() => isFetching.value),
		refetchConfig: refetch,
	};
};

const getMyReports = () => {
	const { isAuthenticated } = useAuthStore();
	const reports = ref<Incident[]>([]);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ['safetyMyReports'],
		queryFn: getMyReportsApiCall,
		enabled: isAuthenticated(),
		refetchInterval: 15000,
		cacheTime: 1000 * 60 * 5,
	});

	watch(
		data,
		() => {
			if (Array.isArray(data.value)) {
				reports.value = data.value;
			}
		},
		{ immediate: true },
	);

	return {
		reports,
		isFetchingReports: computed(() => isFetching.value),
		refetchReports: refetch,
	};
};

const getReport = (incidentId: number) => {
	const report = ref<Incident | null>(null);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ['safetyReport', incidentId],
		queryFn: () => getReportApiCall(incidentId),
		enabled: Number.isFinite(incidentId) && incidentId > 0,
		refetchInterval: 15000,
		cacheTime: 1000 * 60 * 5,
	});

	watch(
		data,
		() => {
			report.value = data.value ?? null;
		},
		{ immediate: true },
	);

	return {
		report,
		isFetchingReport: computed(() => isFetching.value),
		refetchReport: refetch,
	};
};

const getActiveBroadcasts = () => {
	const { isAuthenticated } = useAuthStore();
	const broadcasts = ref<SafetyBroadcast[]>([]);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ['safetyActiveBroadcasts'],
		queryFn: getActiveBroadcastsApiCall,
		enabled: isAuthenticated(),
		refetchInterval: 30000,
		cacheTime: 1000 * 60 * 5,
	});

	watch(
		data,
		() => {
			broadcasts.value = Array.isArray(data.value) ? data.value : [];
		},
		{ immediate: true },
	);

	return {
		broadcasts,
		isFetchingBroadcasts: computed(() => isFetching.value),
		refetchBroadcasts: refetch,
	};
};

const useSafety = () => {
	const { t } = useI18n();
	const queryClient = useQueryClient();
	const { startForegroundTracker } = useGeolocation();

	const reportMutation = useMutation({
		mutationFn: reportIncidentApiCall,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safetyMyReports'] }),
	});

	const sosMutation = useMutation({
		mutationFn: reportSosApiCall,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safetyMyReports'] }),
	});

	const ackBroadcastMutation = useMutation({
		mutationFn: ackBroadcastApiCall,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safetyActiveBroadcasts'] }),
	});

	const reportIncident = async (payload: ReportIncidentPayload): Promise<Incident | null> => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return null;
		}

		const loading = await presentLoading(t('safety.reporting'));
		try {
			const incident = await reportMutation.mutateAsync(payload);
			await presentToast('top', t('safety.reported_successfully'), 3000, 'success');
			loading.dismiss();
			return incident;
		} catch (error: any) {
			const message = isSafetyDisabledError(error) ? t('safety.disabled') : error?.response?.data?.message || t('safety.report_failed');
			await presentToast('top', message, 5000, 'danger');
			loading.dismiss();
			return null;
		}
	};

	const reportSos = async (payload: Pick<ReportIncidentPayload, 'latitude' | 'longitude' | 'gps_accuracy' | 'description'> = {}): Promise<Incident | null> => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return null;
		}

		try {
			const incident = await sosMutation.mutateAsync(payload);
			await presentToast('top', t('safety.sos_sent'), 3000, 'success');
			return incident;
		} catch (error: any) {
			const message = isSafetyDisabledError(error) ? t('safety.disabled') : error?.response?.data?.message || t('safety.sos_failed');
			await presentToast('top', message, 5000, 'danger');
			return null;
		}
	};

	const updateIncidentLocation = async (incidentId: number, coords: GeoCoords): Promise<Incident | null> => {
		try {
			const incident = await updateIncidentLocationApiCall(incidentId, {
				latitude: coords.latitude,
				longitude: coords.longitude,
				gps_accuracy: coords.accuracy,
			});
			queryClient.invalidateQueries({ queryKey: ['safetyReport', incidentId] });
			queryClient.invalidateQueries({ queryKey: ['safetyMyReports'] });
			return incident;
		} catch (error: any) {
			if (isLocationTrackingStoppedError(error) || isSafetyDisabledError(error)) {
				return null;
			}

			throw error;
		}
	};

	const stopSosLocationTracking = () => {
		stopActiveSosTracker?.();
		stopActiveSosTracker = null;
	};

	const startSosLocationTracking = (incidentId: number): void => {
		stopSosLocationTracking();

		stopActiveSosTracker = startForegroundTracker({
			intervalMs: 5000,
			durationMs: 10 * 60 * 1000,
			timeoutMs: 10000,
			maximumAgeMs: 0,
			onPosition: async (coords) => {
				try {
					const incident = await updateIncidentLocation(incidentId, coords);
					return incident !== null;
				} catch (error: any) {
					if (isLocationTrackingStoppedError(error) || isSafetyDisabledError(error)) {
						return false;
					}

					console.warn('safety sos location update failed', error);
					return true;
				}
			},
			onStop: () => {
				stopActiveSosTracker = null;
			},
		});
	};

	const ackBroadcast = async (broadcastId: number): Promise<void> => {
		try {
			await ackBroadcastMutation.mutateAsync(broadcastId);
		} catch (error: any) {
			console.error('failed to ack broadcast', error);
		}
	};

	return {
		getSafetyConfig,
		getMyReports,
		getReport,
		getActiveBroadcasts,
		reportIncident,
		reportSos,
		updateIncidentLocation,
		startSosLocationTracking,
		stopSosLocationTracking,
		ackBroadcast,
		isSafetyDisabledError,
		isLocationTrackingStoppedError,
		isReporting: computed(() => reportMutation.isLoading.value),
		isSendingSos: computed(() => sosMutation.isLoading.value),
	};
};

export default useSafety;
